import { SelectOption } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import { LeaveEmployeeDataFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import RadioGroup from 'modules/core/components/Inputs/RadioGroup'
import TextArea from 'modules/core/components/Inputs/TextArea'
import i18n from 'modules/core/lib/i18n'

import LeaveTypeFormInput from 'modules/timeoff/components/LeaveTypeFormInput'
import { LabourUnitsResult } from 'modules/timeoff/hooks/useLabourUnits'
import type DocumentForm from 'modules/timeoff/lib/DocumentForm'
import type LeaveDayTypeForm from 'modules/timeoff/lib/LeaveDayTypeForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { DaysTree } from 'modules/timeoff/lib/getDateRangeFromTree'
import type { LeaveTypes } from 'modules/timeoff/store/collections/leaveTypes'
import type Leave from 'modules/timeoff/store/models/Leave'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

import DayTypeForm from './DayTypeForm'
import Documents from './Documents'

interface Props {
  readOnly?: boolean
  leaveTypeReadOnly: boolean
  form: LeaveForm
  documentForm: DocumentForm
  customFieldsForms: Form[]
  dayTypeForm: LeaveDayTypeForm
  dayTypeOptions: () => SelectOption[]
  leave?: Leave
  leaveTypes: LeaveTypes
  onDocumentDropped?: () => void
  hasMedicalLeavesOpen?: (employeeId: string) => boolean
  companyHolidaysMuncher: () => DaysTree
  blockedPeriodsMuncher: () => DaysTree
  selectedLeaveType?: LeaveType
  timeTaken?: string
  labourUnits: LabourUnitsResult
  isReviewOrEditMode?: boolean
  leaveCanManageLeaveDocuments: boolean
  employeeCanManageLeavesDocuments: boolean
  leaveEmployee?: LeaveEmployeeDataFragment
}
const OtherLeavesForm = ({
  readOnly = false,
  leaveTypeReadOnly,
  form,
  documentForm,
  customFieldsForms,
  dayTypeForm,
  dayTypeOptions,
  leave,
  leaveTypes,
  onDocumentDropped,
  hasMedicalLeavesOpen,
  companyHolidaysMuncher,
  blockedPeriodsMuncher,
  selectedLeaveType,
  timeTaken,
  labourUnits,
  isReviewOrEditMode,
  leaveCanManageLeaveDocuments,
  employeeCanManageLeavesDocuments,
  leaveEmployee,
}: Props) => {
  const isMandatory = selectedLeaveType?.attributes.details_required
  const leaveId = leave?.id
  const leaveTypeId = form.data().leave_type_id

  return (
    <>
      <LeaveTypeFormInput
        form={form}
        leave={leave}
        availableLeaveTypes={leaveTypes.models}
        readOnly={readOnly || leaveTypeReadOnly}
        hasMedicalLeavesOpen={hasMedicalLeavesOpen}
        selectedLeaveType={selectedLeaveType}
        leaveEmployee={leaveEmployee}
      />
      <FormRow>
        <TextArea
          maxLength={250}
          readonly={readOnly}
          label={i18n.t('time_off.description')}
          field={form.get('description')}
          mandatory={isMandatory}
        />
      </FormRow>
      <FormRow>
        <RadioGroup
          invisibleRadio
          name="dayType"
          theme="toggle"
          readonly={readOnly}
          field={dayTypeForm.get('day_type')}
          options={dayTypeOptions()}
        />
      </FormRow>
      <DayTypeForm
        readOnly={readOnly}
        form={form}
        dayTypeForm={dayTypeForm}
        companyHolidaysMuncher={companyHolidaysMuncher}
        blockedPeriodsMuncher={blockedPeriodsMuncher}
        timeTaken={timeTaken}
        labourUnits={labourUnits}
        isReviewOrEditMode={isReviewOrEditMode}
        leaveId={leaveId}
        leaveTypeId={leaveTypeId}
      />
      {form.acceptsAttachment() && (
        <Documents
          customFieldsForms={customFieldsForms}
          documentForm={documentForm}
          leaveType={leaveTypes.get(form.data().leave_type_id)}
          onDocumentDropped={onDocumentDropped}
          leaveDocuments={leave?.documents || []}
          canManageLeaveDocuments={leaveCanManageLeaveDocuments || employeeCanManageLeavesDocuments}
          currency={leaveEmployee?.legalEntity?.currency}
        />
      )}
    </>
  )
}

export default observer(OtherLeavesForm)
